/**
 * Clinical Data Service
 * Handles all business logic for clinical structure management
 */

export class ClinicalDataService {
  constructor() {
    this.generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  // CRUD Operations
  addItem(structure, type, parentId = null, additionalData = {}) {
    const newItem = {
      id: this.generateId(),
      ...additionalData
    };

    // Map parent relationships correctly
    if (parentId) {
      if (type === 'buildings') {
        newItem.locationId = parentId;
      } else if (type === 'floors') {
        newItem.buildingId = parentId;
      } else if (type === 'departments') {
        newItem.floorId = parentId;
      } else if (type === 'rooms') {
        newItem.departmentId = parentId;
      } else if (type === 'beds') {
        newItem.roomId = parentId;
      } else if (type === 'equipment') {
        newItem.departmentId = parentId;
      }
    }

    // Handle different data types
    const currentData = structure[type];
    
    // If it's an array, add to the array
    if (Array.isArray(currentData)) {
      return {
        ...structure,
        [type]: [...currentData, newItem]
      };
    }
    
    // If it's an object (like company), update the object
    if (currentData && typeof currentData === 'object') {
      return {
        ...structure,
        [type]: { ...currentData, ...newItem }
      };
    }
    
    // If it doesn't exist, create as array
    return {
      ...structure,
      [type]: [newItem]
    };
  }

  updateItem(structure, type, id, updates) {
    const currentData = structure[type];
    
    // Handle different data types
    if (Array.isArray(currentData)) {
      const newStructure = {
        ...structure,
        [type]: currentData.map(item => item.id === id ? { ...item, ...updates } : item)
      };
      
      // Sync assignedPersonnel for departments when personnel departmentId changes
      if (type === 'personnel' && updates.departmentId !== undefined) {
        newStructure.departments = newStructure.departments.map(dept => ({
          ...dept,
          assignedPersonnel: newStructure.personnel
            .filter(person => person.departmentId === dept.id)
            .map(person => person.id)
        }));
      }
      
      return newStructure;
    }
    
    // If it's an object (like company), update the object directly
    if (currentData && typeof currentData === 'object') {
      return {
        ...structure,
        [type]: { ...currentData, ...updates }
      };
    }
    
    return structure;
  }

  deleteItem(structure, type, id) {
    const currentData = structure[type];
    
    // Only proceed if we have array data
    if (!Array.isArray(currentData)) {
      return structure;
    }
    
    // Cascade delete children
    if (type === 'locations') {
      structure.buildings.filter(b => b.locationId === id).forEach(b => structure = this.deleteItem(structure, 'buildings', b.id));
    } else if (type === 'buildings') {
      structure.floors.filter(f => f.buildingId === id).forEach(f => structure = this.deleteItem(structure, 'floors', f.id));
    } else if (type === 'floors') {
      structure.departments.filter(d => d.floorId === id).forEach(d => structure = this.deleteItem(structure, 'departments', d.id));
    } else if (type === 'departments') {
      structure.rooms.filter(r => r.departmentId === id).forEach(r => structure = this.deleteItem(structure, 'rooms', r.id));
      structure.equipment.filter(e => e.departmentId === id).forEach(e => structure = this.deleteItem(structure, 'equipment', e.id));
    } else if (type === 'rooms') {
      structure.beds.filter(b => b.roomId === id).forEach(b => structure = this.deleteItem(structure, 'beds', b.id));
    }

    return {
      ...structure,
      [type]: currentData.filter(item => item.id !== id)
    };
  }

  // Get children relationships
  getBuildings(structure, locationId) {
    return structure.buildings.filter(b => b.locationId === locationId);
  }

  getFloors(structure, buildingId) {
    return structure.floors.filter(f => f.buildingId === buildingId);
  }

  getDepartments(structure, floorId) {
    return structure.departments.filter(d => d.floorId === floorId);
  }

  getRooms(structure, departmentId) {
    return structure.rooms.filter(r => r.departmentId === departmentId);
  }

  getBeds(structure, roomId) {
    return structure.beds.filter(b => b.roomId === roomId);
  }

  getEquipment(structure, departmentId) {
    return structure.equipment.filter(e => e.departmentId === departmentId);
  }

  // Search and filtering
  normalizeString(s) {
    return (s || '').toString().toLowerCase();
  }

  matchesSearch(item, searchQuery) {
    if (!searchQuery) return true;
    const normalizedQuery = this.normalizeString(searchQuery);
    
    // Search in common fields
    const searchableFields = [
      'name', 'number', 'type', 'status', 'address', 'phone', 'email',
      'headDoctor', 'headNurse', 'manager', 'serialNumber', 'manufacturer'
    ];
    
    return searchableFields.some(field => 
      this.normalizeString(item[field]).includes(normalizedQuery)
    );
  }

  // Entity filtering
  filterEntities(structure, filters) {
    const { category, priority, resistance, status } = filters;
    
    return structure.biological_entities.filter(entity => {
      if (category && entity.category !== category) return false;
      if (priority && entity.priority !== priority) return false;
      if (status && entity.detectionStatus !== status) return false;
      if (resistance && entity.resistanceProfile) {
        // Handle both array and object resistanceProfile
        if (Array.isArray(entity.resistanceProfile)) {
          if (!entity.resistanceProfile.some(profile => 
            this.normalizeString(profile).includes(this.normalizeString(resistance))
          )) return false;
        } else if (typeof entity.resistanceProfile === 'object') {
          // For object resistanceProfile, search in JSON string
          if (!JSON.stringify(entity.resistanceProfile).toLowerCase().includes(resistance.toLowerCase())) {
            return false;
          }
        }
      }
      return true;
    });
  }

  // Data validation
  validateItem(type, item) {
    const requiredFields = {
      company: ['name', 'address', 'phone', 'email'],
      locations: ['name', 'address', 'contactPerson', 'phone'],
      buildings: ['name', 'address'],
      floors: ['name', 'number'],
      departments: ['name', 'type', 'headDoctor', 'headNurse'],
      rooms: ['name', 'number', 'type', 'capacity'],
      beds: ['number', 'bedType'],
      equipment: ['name', 'serialNumber', 'manufacturer'],
      personnel: ['name', 'role', 'email', 'phone']
    };

    const fields = requiredFields[type] || [];
    return fields.every(field => item[field] && item[field].toString().trim() !== '');
  }

  // Get default values for new items
  getDefaultValues(type) {
    const defaults = {
      company: {
        entityType: 'multi-specialty',
        totalBeds: 0,
        infectionControlPolicy: {},
        qualityManagementSystem: '',
        riskManagementFramework: '',
        haiTargetRates: {},
        complianceTargets: {},
        qualityIndicators: [],
        patientSafetyGoals: [],
        costPerBed: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      locations: {
        status: 'active',
        emergencyServices: true,
        evacuationPlan: { primary: 'Main Stairs', secondary: 'Emergency Exits' },
        disasterPreparedness: { hurricanes: 'Shelter in place', earthquakes: 'Evacuate to parking lot' },
        airQualityMonitoring: { sensors: 15, frequency: 'continuous' },
        admissionCapacity: { daily: 50, weekly: 300 },
        seasonalVariation: { winter: 1.2, summer: 0.8 },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      buildings: {
        floors: 1,
        yearBuilt: new Date().getFullYear(),
        lastRenovated: new Date().getFullYear(),
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      floors: {
        status: 'active',
        clinicalZoneType: 'general care',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      departments: {
        medicalSpecialty: 'General Medicine',
        parentDepartmentId: '',
        status: 'active',
        infectionControlRiskLevel: 'medium',
        assignedPersonnel: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      rooms: {
        capacity: 1,
        status: 'Active',
        airFiltrationLevel: 'standard',
        surfaceMaterials: ['Standard surfaces'],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      beds: {
        bedType: 'medical',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      equipment: {
        status: 'operational',
        lastMaintenance: new Date().toISOString(),
        nextMaintenance: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        calibrationDue: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        location: 'Main Department',
        purchaseDate: new Date().toISOString(),
        warrantyExpiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        infectionControlRiskLevel: 'low',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      personnel: {
        shift: 'Day',
        certifications: 'Basic certification',
        status: 'active',
        hireDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    };

    return defaults[type] || {};
  }
}

// Export singleton instance
export const clinicalDataService = new ClinicalDataService();
